import { createSelector } from 'reselect'
import { LabRemoteConfigKeys } from 'constants/lab'
import { dateIsAfterOrSame, isValidDate } from 'lib/datetime/dateUtils'
import { isRecord } from 'lib/object/objectUtils'
import { readTrimmedString } from 'loyalty/lib/labPayloadUtils'

export interface LoyaltyPaymentMethodEarnRegion {
  code: string
  /** When the region switches on. Without one, the region is on as soon as the config is enabled. */
  startDate?: Date
}

export interface LoyaltyPaymentMethodEarnConfig {
  enabled: boolean
  regions: ReadonlyArray<LoyaltyPaymentMethodEarnRegion>
}

const DEFAULT_LOYALTY_PAYMENT_METHOD_EARN_CONFIG: LoyaltyPaymentMethodEarnConfig = {
  enabled: false,
  regions: [{ code: 'AU' }],
}

/**
 * Reads one `regions` entry: `{ code, startDate? }`, or a bare region code that is on straight away.
 * A start date that can't be read drops the region, so it stays off rather than switching on early.
 */
function readRegion(entry: unknown): Array<LoyaltyPaymentMethodEarnRegion> {
  const code = readTrimmedString(isRecord(entry) ? entry.code : entry)?.toUpperCase()
  if (!code) return []
  if (!isRecord(entry) || entry.startDate === undefined) return [{ code }]

  const startDateString = readTrimmedString(entry.startDate)
  const startDate = startDateString ? new Date(startDateString) : undefined
  if (!isValidDate(startDate)) return []

  return [{ code, startDate }]
}

/**
 * Reads the Lab remote config that switches on payment-method earn (2x Société points for low cost
 * payment methods), e.g. `{ enabled: true, regions: [{ code: 'AU', startDate: '2026-11-05T07:00:00Z' }] }`.
 * A missing or malformed payload keeps the feature off; a missing `regions` list means AU.
 */
export function parseLoyaltyPaymentMethodEarnConfig(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): LoyaltyPaymentMethodEarnConfig {
  if (!remoteConfig || remoteConfig.error || !isRecord(remoteConfig.value)) {
    return DEFAULT_LOYALTY_PAYMENT_METHOD_EARN_CONFIG
  }

  const { value } = remoteConfig

  return {
    enabled: value.enabled === true,
    regions: Array.isArray(value.regions) ? value.regions.flatMap(readRegion) : DEFAULT_LOYALTY_PAYMENT_METHOD_EARN_CONFIG.regions,
  }
}

/**
 * Whether the config has switched payment-method earn on for a region at `now`. The time comes from
 * the browser clock when the selector recomputes, which in practice is on page load, so a page left
 * open past the start time switches on at its next reload.
 */
export function isLoyaltyPaymentMethodEarnLiveInRegion(
  config: LoyaltyPaymentMethodEarnConfig,
  regionCode: string,
  now = new Date(),
): boolean {
  if (!config.enabled) return false

  const region = config.regions.find(configRegion => configRegion.code === regionCode)
  if (!region) return false

  return !region.startDate || dateIsAfterOrSame(now, region.startDate)
}

const getLoyaltyPaymentMethodEarnConfig = createSelector(
  (state: App.State) => state.system.features.remoteConfigFlags?.[LabRemoteConfigKeys.loyaltyPaymentMethodEarn],
  parseLoyaltyPaymentMethodEarnConfig,
)

export default getLoyaltyPaymentMethodEarnConfig
